import React, { useEffect, useState, useCallback } from "react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import Modal from "../../components/shared/Modal";
import SmartDataTable from "../../components/shared/SmartDataTable";
import { Bell, UsersRound, Loader2, UserX, UserCheck, Eye, Mail, Phone, CalendarDays, ShieldCheck } from "lucide-react";
import { usersApi } from "../../services/api";

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
}

function StatusBadge({ status }) {
  const styles = {
    active:               { background: "rgba(74,222,128,.1)",  color: "var(--green)" },
    suspended:            { background: "rgba(255,180,171,.1)", color: "var(--danger)" },
    pending_verification: { background: "rgba(255,211,119,.1)", color: "var(--gold)" },
  };
  return (
    <em style={{ ...(styles[status] || {}), fontSize: 10, fontWeight: 800, padding: "4px 8px", borderRadius: 4, textTransform: "uppercase", fontStyle: "normal" }}>
      {status?.replace("_", " ")}
    </em>
  );
}

const LIMIT = 25;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await usersApi.getAll({ limit: 1000, page: 1 });
      setUsers(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load users.");
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSuspend(id) {
    setActionId(id);
    try {
      await usersApi.suspend(id);
      setUsers((prev) => prev.map((u) => u._id === id ? { ...u, status: "suspended" } : u));
      if (selected?._id === id) setSelected((u) => ({ ...u, status: "suspended" }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to suspend user.");
    } finally { setActionId(null); }
  }

  async function handleActivate(id) {
    setActionId(id);
    try {
      await usersApi.update(id, { status: "active" });
      setUsers((prev) => prev.map((u) => u._id === id ? { ...u, status: "active" } : u));
      if (selected?._id === id) setSelected((u) => ({ ...u, status: "active" }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to activate user.");
    } finally { setActionId(null); }
  }

  const columns = [
    { name: "Name", selector: (row) => row.name || "", sortable: true, cell: (row) => <strong>{row.name}</strong>, grow: 1.3 },
    { name: "Email", selector: (row) => row.email || "", sortable: true, cell: (row) => <span>{row.email}</span>, grow: 1.7 },
    { name: "Role", selector: (row) => row.role || "", sortable: true, cell: (row) => <span style={{ textTransform: "capitalize" }}>{row.role}</span> },
    { name: "Joined", selector: (row) => row.createdAt || "", sortable: true, cell: (row) => fmtDate(row.createdAt) },
    { name: "Status", selector: (row) => row.status || "", sortable: true, cell: (row) => <StatusBadge status={row.status} /> },
    {
      name: "Actions",
      button: true,
      cell: (row) => (
        <span className="row-actions">
          <button onClick={() => setSelected(row)} title="View details" style={{ color: "var(--gold)" }}>
            <Eye size={14} />
          </button>
          {row.status !== "suspended" ? (
            <button onClick={() => handleSuspend(row._id)} disabled={actionId === row._id} title="Suspend" style={{ color: "var(--danger)" }}>
              {actionId === row._id ? <Loader2 size={14} className="spin" /> : <UserX size={14} />}
            </button>
          ) : (
            <button onClick={() => handleActivate(row._id)} disabled={actionId === row._id} title="Activate" style={{ color: "var(--green)" }}>
              {actionId === row._id ? <Loader2 size={14} className="spin" /> : <UserCheck size={14} />}
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-title">Users</div>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>

        <div className="admin-content">
          {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}
          <section className="admin-panel" style={{ padding: 0 }}>
            <div className="panel-heading">
              <h2><UsersRound size={18} /> All Users
                <span style={{ color: "var(--muted)", fontSize: 13, fontWeight: 400, marginLeft: 8 }}>{total} total</span>
              </h2>
            </div>

            <SmartDataTable
              columns={columns}
              data={users}
              loading={loading}
              searchFields={["name", "email", "role", "status", "phone"]}
              searchPlaceholder="Search users by name, email, role, status..."
              emptyMessage="No users found."
              paginationPerPage={LIMIT}
            />
          </section>
        </div>
      </main>

      {/* ── User Detail Modal ── */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.name || "User Details"} subtitle={selected?.email} icon={<UsersRound size={18} />} size="sm">
        {selected && (
          <div style={{ display: "grid", gap: 20 }}>
            <div className="user-detail-grid">
              <div className="user-detail-row"><span><Mail size={12} style={{ marginRight: 6 }} />Email</span><strong>{selected.email}</strong></div>
              <div className="user-detail-row"><span><Phone size={12} style={{ marginRight: 6 }} />Phone</span><strong>{selected.phone || "—"}</strong></div>
              <div className="user-detail-row"><span><ShieldCheck size={12} style={{ marginRight: 6 }} />Role</span><strong style={{ textTransform: "capitalize" }}>{selected.role}</strong></div>
              <div className="user-detail-row"><span><CalendarDays size={12} style={{ marginRight: 6 }} />Joined</span><strong>{fmtDate(selected.createdAt)}</strong></div>
              <div className="user-detail-row"><span>Status</span><StatusBadge status={selected.status} /></div>
              <div className="user-detail-row"><span>Email Verified</span><strong style={{ color: selected.emailVerified ? "var(--green)" : "var(--danger)" }}>{selected.emailVerified ? "Verified" : "Not verified"}</strong></div>
              <div className="user-detail-row"><span>Subscription</span><strong style={{ textTransform: "capitalize" }}>{selected.subscriptionStatus || "none"}</strong></div>
            </div>
            <div className="modal-actions" style={{ marginTop: 0 }}>
              <button className="modal-btn-cancel" onClick={() => setSelected(null)}>Close</button>
              {selected.status !== "suspended" ? (
                <button className="modal-btn-danger" disabled={actionId === selected._id} onClick={() => handleSuspend(selected._id)}>
                  {actionId === selected._id ? <Loader2 size={14} className="spin" /> : <UserX size={14} />} Suspend User
                </button>
              ) : (
                <button className="modal-btn-submit" disabled={actionId === selected._id} onClick={() => handleActivate(selected._id)}>
                  {actionId === selected._id ? <Loader2 size={14} className="spin" /> : <UserCheck size={14} />} Activate User
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
